import type { ProjectPlan } from '../types/plans';
import { PLANS, PLAN_ORDER, planRank, isUnlimited } from '../types/plans';
import type { OnboardingAnswers } from '../types/accounts';

export type { OnboardingAnswers };

export interface RecommendationReason {
  answer: string;
  requires: ProjectPlan;
}

export interface PlanFit {
  ok: boolean;
  blockers: string[];
}

export interface PlanRecommendation {
  plan: ProjectPlan;
  reasons: RecommendationReason[];
  perPlanFit: Record<ProjectPlan, PlanFit>;
}

export function recommendPlan(answers: OnboardingAnswers): PlanRecommendation {
  const constraints: RecommendationReason[] = [];

  // Helper: record a constraint
  function require(plan: ProjectPlan, answer: string) {
    constraints.push({ answer, requires: plan });
  }

  // 1. Stack checks — find lowest plan that allows each selected stack value
  const stackSlots: (keyof OnboardingAnswers['stacks'])[] = ['backend', 'web', 'mobile', 'windows', 'database'];
  for (const slot of stackSlots) {
    const selected = answers.stacks[slot] ?? [];
    for (const val of selected) {
      if (!val || val === 'none') continue;
      // Find lowest plan that allows this value
      for (const p of PLAN_ORDER) {
        const allowed = PLANS[p].allowedStacks[slot as keyof typeof PLANS[typeof p]['allowedStacks']] ?? [];
        if ((allowed as string[]).includes(val)) {
          if (p !== 'starter') require(p, `${slot}: ${val}`);
          break;
        }
      }
    }
  }

  // 2. repoCount → maxConnectedRepos
  const repoUpperBound: Record<string, number> = { '1': 1, '2-3': 3, '4-15': 15, '15+': 999 };
  const repoNeeded = repoUpperBound[answers.repoCount] ?? 1;
  for (const p of PLAN_ORDER) {
    const limit = PLANS[p].limits.maxConnectedRepos;
    if (isUnlimited(limit) || limit >= repoNeeded) {
      if (p !== 'starter') require(p, `${answers.repoCount} repos`);
      break;
    }
  }

  // 3. orgCount '2+' → business (unlimited orgs)
  if (answers.orgCount === '2+') {
    for (const p of PLAN_ORDER) {
      if (isUnlimited(PLANS[p].limits.maxOrgs)) {
        require(p, '2+ GitHub organizations');
        break;
      }
    }
  }

  // 4. projectCount → maxProjects
  const projUpperBound: Record<string, number> = { '1': 1, '2-3': 3, '4-10': 10, '10+': 999 };
  const projNeeded = projUpperBound[answers.projectCount] ?? 1;
  for (const p of PLAN_ORDER) {
    const limit = PLANS[p].limits.maxProjects;
    if (isUnlimited(limit) || limit >= projNeeded) {
      if (p !== 'starter') require(p, `${answers.projectCount} projects`);
      break;
    }
  }

  // 5. testDeploys / browserSmoke → business
  if (answers.needs.testDeploys) {
    for (const p of PLAN_ORDER) {
      if (PLANS[p].features.feature_test_deploy) {
        require(p, 'test environment deploys');
        break;
      }
    }
  }
  if (answers.needs.browserSmoke) {
    for (const p of PLAN_ORDER) {
      if (PLANS[p].features.feature_browser_smoke) {
        require(p, 'browser smoke tests');
        break;
      }
    }
  }

  // 6. compliance frameworks
  for (const framework of answers.needs.compliance) {
    for (const p of PLAN_ORDER) {
      if ((PLANS[p].features.feature_custom_compliance as string[]).includes(framework)) {
        require(p, `${framework} compliance`);
        break;
      }
    }
  }

  // 7. providers
  for (const provider of answers.needs.providers) {
    for (const p of PLAN_ORDER) {
      if ((PLANS[p].providers as string[]).includes(provider)) {
        require(p, `${provider} LLM provider`);
        break;
      }
    }
  }

  // 8. SSO/SCIM → enterprise
  if (answers.needs.ssoScim) {
    for (const p of PLAN_ORDER) {
      if (PLANS[p].features.feature_sso_scim) {
        require(p, 'SSO/SCIM');
        break;
      }
    }
  }

  // Compute recommended plan = max of all constraint minimums
  const recommended: ProjectPlan = constraints.reduce<ProjectPlan>(
    (best, c) => planRank(c.requires) > planRank(best) ? c.requires : best,
    'starter',
  );

  // Compute per-plan fit
  const perPlanFit = {} as Record<ProjectPlan, PlanFit>;
  for (const p of PLAN_ORDER) {
    const blockers: string[] = constraints
      .filter((c) => planRank(c.requires) > planRank(p))
      .map((c) => c.answer);
    perPlanFit[p] = { ok: blockers.length === 0, blockers };
  }

  // Only keep reasons that are the actual binding constraint (drove the final plan)
  const reasons = constraints.filter((c) => c.requires === recommended);

  return { plan: recommended, reasons, perPlanFit };
}
